/**
 * AI CONTEXT: security - AWS Cognito configuration for web application
 * 
 * SECURITY NOTE: This file contains public identifiers only.
 * User Pool ID and Client ID are designed to be visible in client-side code.
 * Real security is provided by user credentials and AWS IAM policies.
 * 
 * This configuration provides AWS Cognito setup with:
 * - User pool and client configuration
 * - Security-appropriate settings for web applications
 * - Environment variable mapping
 * - No client secret (recommended for web apps)
 * 
 * Architecture decisions:
 * - Remove client secret for web application security
 * - Use environment variables for configuration
 * - Support both development and production environments
 * - Keep only public identifiers in client-side code
 * 
 * Future considerations:
 * - Add environment-specific configurations
 * - Implement secure secret management
 * - Consider using AWS Secrets Manager for sensitive data
 * - Add runtime environment validation
 */

// AWS Cognito Configuration
// SECURITY: These are public identifiers, safe for client-side use
export const awsConfig = {
  region: process.env.REACT_APP_AWS_REGION || 'us-east-2',
  userPoolId: process.env.REACT_APP_USER_POOL_ID || 'us-east-2_VmOI3lMhR',
  userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '2mnpqgggr33e46u92kqrv8cchb',
  // Note: Client secret removed for web application security
  // Web applications should not use client secrets
};

// AI CONTEXT: payments - Stripe test configuration (public key only)
// Reasoning: Initialize Stripe.js with publishable key on client; secret key remains on server (Lambda)
export const paymentsConfig = {
  stripePublishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
};

// Environment variables for React app
export const envConfig = {
  REACT_APP_AWS_REGION: awsConfig.region,
  REACT_APP_USER_POOL_ID: awsConfig.userPoolId,
  REACT_APP_USER_POOL_CLIENT_ID: awsConfig.userPoolClientId
};

// Security validation
export const validateSecurityConfig = () => {
  const required = ['userPoolId', 'userPoolClientId', 'region'];
  const missing = required.filter(key => !awsConfig[key]);
  
  if (missing.length > 0) {
    console.warn('Security Warning: Missing required AWS configuration:', missing);
    return false;
  }
  
  // Validate that we're not accidentally exposing secrets
  const sensitiveKeys = ['userPoolClientSecret', 'secret', 'password', 'key'];
  const hasSensitiveData = Object.keys(awsConfig).some(key => 
    sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))
  );
  
  if (hasSensitiveData) {
    console.error('Security Error: Sensitive data detected in client-side config');
    return false;
  }
  
  return true;
}; 